import {
  Container,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useState } from "react";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { getHeader, toastError } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

const AddCredit = ({ id, rechargeType }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      amount: "",
      remarks: "",
      type: "credit",
      expiryDate: "",
    },
  });
  const [submitting, setSubmitting] = useState(false);

  const transactionType = watch("type");

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      const payload = {
        amount: parseFloat(data.amount),
        remarks: data.remarks,
        type: data.type,
        rechargeType,
        expiryDate: data.expiryDate, // Send expiry date to backend
      };

      const response = await axios.post(
        `${API_ENDPOINT}/credit/add-credit/${id}`,
        payload,
        getHeader()
      );
      toast.success(response.data.message);
      reset();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split("T")[0];

  return (
    <Container maxWidth="sm">
      <PageTitle>
        {transactionType === "credit"
          ? `Add ${rechargeType} Credit`
          : `Add ${rechargeType} Debit`}
      </PageTitle>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ mt: 3 }}
      >
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Transaction Type</InputLabel>
          <Select
            value={transactionType}
            label="Transaction Type *"
            onChange={(e) => setValue("type", e.target.value)}
            error={Boolean(errors.type)}
          >
            <MenuItem value="credit">Credit</MenuItem>
            <MenuItem value="debit">Debit</MenuItem>
          </Select>
          {errors.type && (
            <Typography color="error" variant="caption">
              {errors.type.message}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label={`${transactionType === "credit" ? "Credit" : "Debit"} Amount`}
          type="number"
          inputProps={{
            step: "0.01",
            min: "0.01",
          }}
          {...register("amount", {
            required: "Amount is required",
            min: {
              value: 0.01,
              message: "Amount must be greater than 0",
            },
            pattern: {
              value: /^\d+(\.\d{1,2})?$/,
              message: "Invalid amount format (e.g. 10.50)",
            },
          })}
          error={Boolean(errors.amount)}
          helperText={errors.amount?.message}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Expiry Date"
          type="date"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: today,
          }}
          {...register("expiryDate", {
            required: "Expiry date is required",
            validate: (value) => {
              const selectedDate = new Date(value);
              const todayDate = new Date(today);
              if (selectedDate < todayDate) {
                return "Expiry date cannot be in the past";
              }
              return true;
            },
          })}
          error={Boolean(errors.expiryDate)}
          helperText={errors.expiryDate?.message}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Remarks"
          multiline
          rows={4}
          {...register("remarks", {
            required: "Remarks are required",
            maxLength: {
              value: 500,
              message: "Remarks should not exceed 500 characters",
            },
          })}
          error={Boolean(errors.remarks)}
          helperText={errors.remarks?.message}
        />

        <LoadingButton
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          loading={submitting}
        >
          {transactionType === "credit" ? "Submit Credit" : "Submit Debit"}
        </LoadingButton>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {transactionType === "credit" ? "Adding credit" : "Adding debit"} for
          user ID: {id}
        </Typography>
      </Box>
    </Container>
  );
};

export default AddCredit;
